import * as THREE from 'three';

// Singleton pattern for scene management
export class SceneManager {
    private static instance: SceneManager;
    private scene: THREE.Scene;
    private camera!: THREE.PerspectiveCamera;
    private renderer!: THREE.WebGLRenderer;
    private canvas!: HTMLCanvasElement;

    private constructor() {
        this.scene = new THREE.Scene();
        this.initializeCamera();
        this.initializeRenderer();
        this.setupLighting();
        this.resize();
    }

    public static getInstance(): SceneManager {
        if (!SceneManager.instance) {
            SceneManager.instance = new SceneManager();
        }
        return SceneManager.instance;
    }

    private initializeCamera(): void {
        this.camera = new THREE.PerspectiveCamera(45, 2, 0.1, 1000);
        this.camera.position.set(0, 0, 3.5);
    }

    private initializeRenderer(): void {
        this.canvas = document.getElementById('canvas') as HTMLCanvasElement;
        this.renderer = new THREE.WebGLRenderer({ 
            canvas: this.canvas,
            antialias: true,
            alpha: true
        });
        
        const container = this.canvas.parentElement;
        if (container) {
            const width = container.clientWidth;
            const height = container.clientHeight;
            this.renderer.setSize(width, height);
        } else {
            this.renderer.setSize(800, 400);
        }
        
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.setClearColor(0x000000, 0);
    }

    private setupLighting(): void {
        const ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
        this.scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(-1, 3, 2);
        this.scene.add(directionalLight);

        const fillLight = new THREE.DirectionalLight(0xffffff, 0.5);
        fillLight.position.set(2, -1, 2);
        this.scene.add(fillLight);
        
        const backLight = new THREE.DirectionalLight(0xffffff, 0.4);
        backLight.position.set(0, 0, -3);
        this.scene.add(backLight);
    }

    public addToScene(object: THREE.Object3D): void {
        this.scene.add(object);
    }

    public removeFromScene(object: THREE.Object3D): void {
        this.scene.remove(object);
    }

    public render(): void {
        this.renderer.render(this.scene, this.camera);
    }

    public getCamera(): THREE.PerspectiveCamera {
        return this.camera;
    }

    public getRenderer(): THREE.WebGLRenderer {
        return this.renderer;
    }

    public getScene(): THREE.Scene {
        return this.scene;
    }

    public resize(): void {
        const container = this.canvas.parentElement;
        if (container) {
            const width = container.clientWidth;
            const height = container.clientHeight;
            
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
            this.renderer.setSize(width, height);
        }
    }
}
